import type { Database } from 'sql.js';
import { asObjectArray, pickPublicId, readNumberOrNull } from '@database/catalogRowHelpers.mjs';
import type { RecordSyncStatus } from '@src/modules/kernel/PendingSyncRecord.ts';
import { markLocalSqliteDirty } from '@src/modules/platform/local-sqlite/infrastructure/localSqlitePersistDirty.ts';

export type { GetSqlJsDatabase } from '@src/modules/platform/local-sqlite/domain/GetSqlJsDatabase.ts';

export function readString(row: Record<string, unknown>, key: string): string {
  const v = row[key];
  return typeof v === 'string' ? v : '';
}

export function readInt(row: Record<string, unknown>, key: string): number {
  const v = row[key];
  if (typeof v === 'number' && Number.isFinite(v)) return Math.trunc(v);
  if (typeof v === 'string' && v.trim()) return Math.trunc(Number(v)) || 0;
  return 0;
}

export { pickPublicId, asObjectArray, readNumberOrNull };

export function inClausePlaceholders(count: number): string {
  return count > 0 ? Array.from({ length: count }, () => '?').join(', ') : "''";
}

export function markRowsSynced(db: Database, table: string, publicIds: string[]): void {
  if (publicIds.length === 0) return;
  for (const publicId of publicIds) {
    db.run(
      `UPDATE ${table} SET sync_status = 'synced', sync_error = NULL, fecha_actualizacion = datetime('now') WHERE public_id = ?`,
      [publicId],
    );
  }
  markLocalSqliteDirty();
}

export function markRowFailed(db: Database, table: string, publicId: string, message: string): void {
  db.run(
    `UPDATE ${table} SET sync_status = 'failed', sync_error = ?, fecha_actualizacion = datetime('now') WHERE public_id = ?`,
    [message.slice(0, 500), publicId],
  );
  markLocalSqliteDirty();
}

/** Soft note on card: keep synced, otherwise pending — never failed. */
export function markRowSyncWarning(db: Database, table: string, publicId: string, message: string): void {
  db.run(
    `UPDATE ${table}
     SET sync_status = CASE WHEN sync_status = 'synced' THEN 'synced' ELSE 'pending' END,
         sync_error = ?,
         fecha_actualizacion = datetime('now')
     WHERE public_id = ?`,
    [message.slice(0, 500), publicId],
  );
  markLocalSqliteDirty();
}

export function normalizeSyncStatus(raw: string): RecordSyncStatus {
  return raw === 'synced' || raw === 'failed' ? raw : 'pending';
}

export function runSqlJsTransaction<T>(db: Database, fn: () => T): T {
  db.run('BEGIN');
  try {
    const result = fn();
    db.run('COMMIT');
    markLocalSqliteDirty();
    return result;
  } catch (e) {
    db.run('ROLLBACK');
    throw e;
  }
}

/** Escape `%`, `_`, `\` for SQL LIKE … ESCAPE '\\'. */
export function escapeSqlLike(raw: string): string {
  return raw.replace(/[%_\\]/g, (c) => `\\${c}`);
}

/** Strip doc punctuation so `123456789` matches stored `12345678-9`. */
export function normalizeBeneficiarioDocForSearch(raw: string): string {
  return raw.replace(/[\s.\-_/]/g, '').toLowerCase();
}

/** Fold Spanish accents so `Jose` matches `José`. */
export function foldSpanishForSearch(raw: string): string {
  let s = raw.toLowerCase();
  for (const [from, to] of [
    ['á', 'a'],
    ['é', 'e'],
    ['í', 'i'],
    ['ó', 'o'],
    ['ú', 'u'],
    ['ü', 'u'],
    ['ñ', 'n'],
  ] as const) {
    s = s.replaceAll(from, to);
  }
  return s;
}

const BENEFICIARIO_DOC_NORM_SQL = `LOWER(REPLACE(REPLACE(REPLACE(REPLACE(b.documento, '.', ''), '-', ''), ' ', ''), '/', ''))`;

const SPANISH_FOLD_PAIRS = [
  ['á', 'a'],
  ['é', 'e'],
  ['í', 'i'],
  ['ó', 'o'],
  ['ú', 'u'],
  ['ü', 'u'],
  ['ñ', 'n'],
] as const;

function sqlFoldSpanishExpr(expr: string): string {
  let s = expr;
  for (const [from, to] of SPANISH_FOLD_PAIRS) {
    s = `REPLACE(${s}, '${from}', '${to}')`;
  }
  return s;
}

const BENEFICIARIO_NOMBRE_FOLD_SQL = sqlFoldSpanishExpr('LOWER(b.nombre)');

const BENEFICIARIO_ITEM_FILTER_SQL = `(
  ? = ''
  OR EXISTS (
    SELECT 1 FROM beneficiario_item bi1
    WHERE bi1.beneficiario_public_id = b.public_id AND bi1.item_entrega_public_id = ?
  )
)`;

function beneficiarioDocDigitsPrefix(query: string): string | null {
  const n = normalizeBeneficiarioDocForSearch(query);
  return n.length >= 2 && /^\d+$/.test(n) ? n : null;
}

function queryBeneficiariosByDocPrefix(
  db: Database,
  docPrefix: string,
  itemPublicId: string,
  extraSelectCols: string[],
): Record<string, unknown>[] {
  const itemId = itemPublicId.trim();
  const prefixLike = `${escapeSqlLike(docPrefix)}%`;
  const extra = extraSelectCols.length > 0 ? `, ${extraSelectCols.map((c) => `b.${c}`).join(', ')}` : '';
  const stmt = db.prepare(
    `SELECT b.public_id, b.documento, b.nombre${extra}
     FROM beneficiarios b
     WHERE b.estado = 1
       AND ${BENEFICIARIO_DOC_NORM_SQL} LIKE ? ESCAPE '\\'
       AND ${BENEFICIARIO_ITEM_FILTER_SQL}
     ORDER BY b.documento COLLATE NOCASE LIMIT 20`,
  );
  stmt.bind([prefixLike, itemId, itemId]);
  const rows: Record<string, unknown>[] = [];
  while (stmt.step()) rows.push(stmt.getAsObject() as Record<string, unknown>);
  stmt.free();
  return rows;
}

function queryBeneficiariosContains(
  db: Database,
  query: string,
  itemPublicId: string,
  extraSelectCols: string[],
): Record<string, unknown>[] {
  const itemId = itemPublicId.trim();
  const nameLike = `%${escapeSqlLike(foldSpanishForSearch(query))}%`;
  const normLike = `%${escapeSqlLike(normalizeBeneficiarioDocForSearch(query))}%`;
  const extra = extraSelectCols.length > 0 ? `, ${extraSelectCols.map((c) => `b.${c}`).join(', ')}` : '';
  const stmt = db.prepare(
    `SELECT b.public_id, b.documento, b.nombre${extra}
     FROM beneficiarios b
     WHERE b.estado = 1
       AND (${BENEFICIARIO_NOMBRE_FOLD_SQL} LIKE ? ESCAPE '\\' OR ${BENEFICIARIO_DOC_NORM_SQL} LIKE ? ESCAPE '\\')
       AND ${BENEFICIARIO_ITEM_FILTER_SQL}
     ORDER BY b.documento COLLATE NOCASE LIMIT 20`,
  );
  stmt.bind([nameLike, normLike, itemId, itemId]);
  const rows: Record<string, unknown>[] = [];
  while (stmt.step()) rows.push(stmt.getAsObject() as Record<string, unknown>);
  stmt.free();
  return rows;
}

export function queryLocalBeneficiarios(
  db: Database,
  query: string,
  itemPublicId = '',
  extraSelectCols: string[] = [],
): Record<string, unknown>[] {
  const q = query.trim();
  const itemId = itemPublicId.trim();
  if (!q || q.length < 2) return [];
  const docPrefix = beneficiarioDocDigitsPrefix(q);
  if (docPrefix) {
    const byDoc = queryBeneficiariosByDocPrefix(db, docPrefix, itemId, extraSelectCols);
    if (byDoc.length > 0) return byDoc;
  }
  return queryBeneficiariosContains(db, q, itemId, extraSelectCols);
}

export function queryPublicIdNombreTable(db: Database, table: string): { publicId: string; nombre: string }[] {
  const stmt = db.prepare(`SELECT public_id, nombre FROM ${table} WHERE estado = 1 ORDER BY nombre COLLATE NOCASE`);
  const rows: { publicId: string; nombre: string }[] = [];
  while (stmt.step()) {
    const row = stmt.getAsObject() as Record<string, unknown>;
    rows.push({ publicId: readString(row, 'public_id'), nombre: readString(row, 'nombre') });
  }
  stmt.free();
  return rows;
}
